import React, { useMemo } from "react";
import { CheckCircle2, Circle, Lock, Flag, Clock, FileDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { roadmapPhases } from "@/data/roadmap";
import { ProgressBar } from "@/components/ProgressRing";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";

export default function Roadmap() {
  const { data, updateData, displayName } = useApp();
  const completed = data.completedTasks;

  const allTasks = useMemo(() => roadmapPhases.flatMap((p) => p.tasks), []);
  const total = allTasks.length;
  const doneCount = allTasks.filter((t) => completed.includes(t.task_id)).length;
  const pct = Math.round((doneCount / total) * 100);

  const isLocked = (t) => t.dependency_ids.some((dep) => !completed.includes(dep));
  const todayTask = allTasks.find((t) => !completed.includes(t.task_id) && !isLocked(t));

  const exportPdf = () => {
    const esc = (x) => String(x).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const today = new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
    const phasesHtml = roadmapPhases.map((p) => {
      const tasks = p.tasks.map((t) => {
        const done = completed.includes(t.task_id);
        return `<li class="${done ? "done" : ""}"><span class="chk">${done ? "&#10003;" : "&#9711;"}</span>
          <span><b>${esc(t.title)}</b> <span class="pri ${t.priority}">${t.priority}</span><br/>
          <span class="reason">${esc(t.reason)} &middot; Estimasi: ${esc(t.estimated_effort)}</span></span></li>`;
      }).join("");
      return `<section><h3><span class="badge">${p.id}</span> ${esc(p.title)}</h3><ul>${tasks}</ul></section>`;
    }).join("");

    const html = `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Roadmap Persiapan Scholavia</title>
      <style>
        *{box-sizing:border-box} body{font-family:'Inter',Arial,sans-serif;color:#1e1b4b;margin:0;padding:40px;background:#fff}
        .head{border-bottom:3px solid #7C3AED;padding-bottom:16px;margin-bottom:8px}
        .brand{font-size:24px;font-weight:800;color:#4C1D95}.brand span{color:#8B5CF6}
        h1{font-size:20px;margin:12px 0 2px}.meta{color:#64748b;font-size:12px}
        .prog{margin:16px 0;padding:12px 16px;background:#F5F3FF;border-radius:12px;font-size:14px;color:#4C1D95;font-weight:600}
        section{margin:14px 0;break-inside:avoid}
        h3{font-size:15px;margin:0 0 8px;color:#111}
        .badge{display:inline-block;width:22px;height:22px;line-height:22px;text-align:center;background:#7C3AED;color:#fff;border-radius:6px;font-size:12px;margin-right:6px}
        ul{list-style:none;padding:0;margin:0;border-left:2px solid #EDE9FE;padding-left:16px}
        li{display:flex;gap:8px;padding:8px 0;font-size:13px;border-bottom:1px solid #f4f0ff;align-items:flex-start}
        li.done b{text-decoration:line-through;color:#94a3b8}
        .chk{color:#7C3AED;font-weight:700}.reason{color:#64748b;font-size:11px}
        .pri{font-size:9px;padding:1px 6px;border-radius:6px;font-weight:700}
        .pri.High{background:#fef2f2;color:#dc2626}.pri.Medium{background:#fffbeb;color:#d97706}
        .foot{margin-top:24px;font-size:10px;color:#94a3b8;border-top:1px solid #eee;padding-top:10px}
      </style></head><body>
      <div class="head"><div class="brand">Schola<span>via</span></div>
      <h1>Roadmap Persiapan Studi ke Luar Negeri</h1>
      <div class="meta">${esc(displayName || "Pengguna")} &middot; Dibuat ${today}</div></div>
      <div class="prog">Progres: ${doneCount}/${total} tugas selesai (${pct}%)</div>
      ${phasesHtml}
      <div class="foot">Dokumen dihasilkan oleh Scholavia. Roadmap ini adalah alat bantu persiapan, verifikasi setiap syarat & deadline ke sumber resmi.</div>
      </body></html>`;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const toggle = (t) => {
    if (isLocked(t) && !completed.includes(t.task_id)) return;
    updateData((prev) => {
      const done = prev.completedTasks.includes(t.task_id);
      return { ...prev, completedTasks: done ? prev.completedTasks.filter((x) => x !== t.task_id) : [...prev.completedTasks, t.task_id] };
    });
  };

  return (
    <div className="space-y-6">
      <SectionTitle title="Roadmap Persiapan" desc="Rencana langkah demi langkah menuju kampus impianmu."
        action={<Button onClick={exportPdf} data-testid="export-roadmap-pdf" variant="outline" className="rounded-full border-purple-200 text-[#5B21B6] hover:bg-purple-50"><FileDown className="mr-1.5 h-4 w-4" /> Ekspor PDF</Button>} />

      {/* Today */}
      <div className="rounded-2xl border border-transparent bg-gradient-to-r from-[#5B21B6] to-[#8B5CF6] p-5 text-white shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-purple-200">Apa yang harus saya lakukan hari ini?</p>
        <h2 className="mt-1 font-display text-xl font-bold">{todayTask ? todayTask.title : "🎉 Semua langkah prioritas selesai!"}</h2>
        {todayTask && <p className="mt-1 text-sm text-purple-100">{todayTask.reason} · Estimasi: {todayTask.estimated_effort}</p>}
      </div>

      {/* Progress */}
      <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between text-sm"><span className="font-medium text-slate-600">Progres keseluruhan</span><span className="font-bold text-[#5B21B6]">{doneCount}/{total} tugas</span></div>
        <ProgressBar value={pct} className="mt-2" />
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {roadmapPhases.map((phase) => {
          const phaseDone = phase.tasks.filter((t) => completed.includes(t.task_id)).length;
          return (
            <div key={phase.id} className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#7C3AED] text-xs font-bold text-white">{phase.id}</span>
                <h3 className="font-display text-base font-bold text-slate-900">{phase.title}</h3>
                <span className="ml-auto text-xs text-slate-400">{phaseDone}/{phase.tasks.length}</span>
              </div>
              <div className="space-y-2 border-l-2 border-purple-100 pl-4">
                {phase.tasks.map((t) => {
                  const done = completed.includes(t.task_id);
                  const locked = isLocked(t) && !done;
                  return (
                    <div key={t.task_id} className="relative">
                      <span className="absolute -left-[22px] top-1.5 h-3 w-3 rounded-full border-2 border-white" style={{ background: done ? "#10B981" : locked ? "#CBD5E1" : "#A78BFA" }} />
                      <button onClick={() => toggle(t)} disabled={locked} data-testid={`roadmap-task-${t.task_id}`}
                        className={`flex w-full items-start gap-2.5 rounded-xl border p-3 text-left transition-all ${locked ? "cursor-not-allowed border-slate-100 bg-slate-50 opacity-70" : "border-purple-50 hover:border-purple-200 hover:bg-purple-50/40"}`}>
                        {done ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" /> : locked ? <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /> : <Circle className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" />}
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`text-sm font-medium ${done ? "text-slate-400 line-through" : "text-slate-700"}`}>{t.title}</span>
                            <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${t.priority === "High" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>{t.priority}</span>
                          </div>
                          <p className="mt-0.5 text-xs text-slate-400">{t.reason}</p>
                          <p className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
                            <Clock className="h-3 w-3" /> {t.estimated_effort}
                            {locked && <span className="text-orange-500">· Terkunci: selesaikan prasyarat dulu</span>}
                          </p>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
